import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useLocation } from '../contexts/LocationContext';

const LocationModal: React.FC = () => {
  const { locationStatus, requestLocation, denyLocation, isLoading, error, isInitialized } = useLocation();
  const [open, setOpen] = useState(false);
  const [permissionRevoked, setPermissionRevoked] = useState(false);

  useEffect(() => {
    // Only show modal if location status is 'prompt' AND initialization is complete
    if (locationStatus === 'prompt' && isInitialized) {
      setOpen(true);
      
      // Check if permission was previously granted but now needs to be requested again
      const wasGranted = localStorage.getItem('locationPermission') === 'granted';
      setPermissionRevoked(wasGranted);
    } else {
      setOpen(false);
    }
  }, [locationStatus, isInitialized]);

  const handleAllow = async () => {
    try {
      await requestLocation();
    } catch (error: unknown) {
      console.error('Error requesting location:', error);
    }
  };

  const handleDeny = () => {
    denyLocation();
    setOpen(false);
  };

  // Don't render anything until initialization is complete
  if (!isInitialized) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleDeny}
      aria-labelledby="location-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="location-dialog-title" sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center">
          <LocationOnIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">
            {permissionRevoked 
              ? 'Location Permission Required' 
              : 'Enable Location Services'}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {permissionRevoked ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            It appears you've revoked location permission in your browser. We need your permission again to show events near you.
          </Alert>
        ) : (
          <Typography variant="body1" sx={{ mb: 2 }}>
            We use your location to show you events happening near you and provide better recommendations.
          </Typography>
        )}
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Your location data is only used to enhance your experience and will not be shared with third parties.
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {permissionRevoked && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            To allow location access:
            <ol>
              <li>Click "Allow" below</li>
              <li>When your browser shows the permission dialog, select "Allow"</li>
              <li>If no dialog appears, you may need to reset permissions in your browser settings</li>
            </ol>
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={handleDeny} 
          color="inherit"
          variant="outlined"
          disabled={isLoading}
        >
          Not Now
        </Button>
        <Button 
          onClick={handleAllow} 
          color="primary" 
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isLoading ? 'Requesting...' : 'Allow Location Access'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocationModal; 